(() => {
  if (window.PlaygeniCartDiscountReady) return;
  window.PlaygeniCartDiscountReady = true;

  const root = () => window.Shopify?.routes?.root || '/';
  let busy = false;

  const escapeHTML = (value) => {
    const element = document.createElement('div');
    element.textContent = String(value || '');
    return element.innerHTML;
  };

  const getCart = async () => {
    const response = await fetch(`${root()}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Unable to load the cart.');
    return response.json();
  };

  const getApplicableCodes = (cart) => {
    if (!Array.isArray(cart?.discount_codes)) return [];
    return cart.discount_codes.filter((discount) => discount?.code && discount.applicable !== false);
  };

  const setError = (wrapper, message = '') => {
    const error = wrapper?.querySelector('[data-pg-cart-discount-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  };

  const pillMarkup = (code) => `
    <li class="pg-cart-discount__pill" data-discount-code="${escapeHTML(code)}" aria-label="Applied discount code: ${escapeHTML(code)}">
      <p class="pg-cart-discount__pill-code">${escapeHTML(code)}</p>
      <button
        type="button"
        class="pg-cart-discount__pill-remove"
        data-pg-remove-discount="${escapeHTML(code)}"
        aria-label="Remove discount ${escapeHTML(code)}"
      >
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true" pointer-events="none">
          <path d="M1 1L7 7M7 1L1 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
    </li>`;

  const renderCodes = (cart) => {
    const codes = getApplicableCodes(cart);
    document.querySelectorAll('[data-pg-cart-discount]').forEach((wrapper) => {
      const list = wrapper.querySelector('[data-pg-cart-discount-codes]');
      const details = wrapper.querySelector('details');
      if (list) list.innerHTML = codes.map((discount) => pillMarkup(discount.code)).join('');
      if (details && codes.length) details.open = true;
    });
  };

  const refreshCodes = async () => {
    try {
      const cart = await getCart();
      renderCodes(cart);
      return cart;
    } catch (_) {
      return null;
    }
  };

  const updateDiscounts = async (codes) => {
    const response = await fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: codes.join(',') }),
    });
    const cart = await response.json();
    if (!response.ok) {
      throw new Error(cart?.description || cart?.message || 'Discount code cannot be updated.');
    }
    return cart;
  };

  const refreshThemeCart = () => {
    document.dispatchEvent(new CustomEvent('theme:update:cart', { bubbles: true }));
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-pg-cart-discount-form]');
    if (!form || busy) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const wrapper = form.closest('[data-pg-cart-discount]');
    const input = form.querySelector('input[name="discount"]');
    const button = form.querySelector('button[type="submit"]');
    const code = input?.value.trim() || '';

    if (!code) {
      setError(wrapper, 'Please enter a discount code.');
      input?.focus();
      return;
    }

    busy = true;
    setError(wrapper);
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    try {
      const currentCart = await getCart();
      const currentCodes = getApplicableCodes(currentCart).map((discount) => discount.code);
      const alreadyApplied = currentCodes.some((currentCode) => currentCode.toLowerCase() === code.toLowerCase());
      const requestedCodes = alreadyApplied ? currentCodes : [...currentCodes, code];
      const updatedCart = await updateDiscounts(requestedCodes);
      const applied = getApplicableCodes(updatedCart).some((discount) => discount.code.toLowerCase() === code.toLowerCase());
      if (!applied) throw new Error('Discount code cannot be applied to your cart.');
      if (input) input.value = '';
      renderCodes(updatedCart);
      refreshThemeCart();
    } catch (error) {
      setError(wrapper, error?.message || 'Discount code cannot be applied to your cart.');
    } finally {
      busy = false;
      if (button?.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }, true);

  document.addEventListener('click', async (event) => {
    const removeButton = event.target.closest('[data-pg-remove-discount]');
    if (!removeButton || busy) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const wrapper = removeButton.closest('[data-pg-cart-discount]');
    const codeToRemove = removeButton.getAttribute('data-pg-remove-discount') || '';
    if (!codeToRemove) return;

    busy = true;
    removeButton.disabled = true;
    removeButton.setAttribute('aria-busy', 'true');
    setError(wrapper);

    try {
      const currentCart = await getCart();
      const remainingCodes = getApplicableCodes(currentCart)
        .map((discount) => discount.code)
        .filter((code) => code.toLowerCase() !== codeToRemove.toLowerCase());

      await updateDiscounts(remainingCodes);
      const verifiedCart = await getCart();
      const stillApplied = getApplicableCodes(verifiedCart).some(
        (discount) => discount.code.toLowerCase() === codeToRemove.toLowerCase()
      );
      if (stillApplied) throw new Error('Discount code could not be removed.');

      renderCodes(verifiedCart);
      refreshThemeCart();
    } catch (error) {
      setError(wrapper, error?.message || 'Discount code could not be removed.');
      if (removeButton.isConnected) {
        removeButton.disabled = false;
        removeButton.removeAttribute('aria-busy');
      }
    } finally {
      busy = false;
    }
  }, true);

  window.addEventListener('shapes:cart:update', () => window.requestAnimationFrame(refreshCodes));
  window.addEventListener('shapes:cart:afteradditem', () => window.requestAnimationFrame(refreshCodes));
  window.addEventListener('shapes:cart:cartqtychange', () => window.requestAnimationFrame(refreshCodes));
  document.addEventListener('theme:cart-drawer:open', refreshCodes);
  document.addEventListener('DOMContentLoaded', refreshCodes);
})();
